import { NJAutocomplete, NJInlineMessage, NJSegmentedControl, NJSegmentedControlButton } from '@engie-group/fluid-design-system-react';
import styles from './ChatEquipmentSelect.module.css';
import { useCallback, useEffect, useRef, useState } from 'react';
import { equipmentType, EquipmentType } from '../model';

export type EquipmentAutoComplete = {
  name: string;
  value: string;
};

export interface IChatEquipmentSelectProps {
  brands?: EquipmentAutoComplete[];
  models?: EquipmentAutoComplete[];
  onBrandSelectionChange: (v: EquipmentAutoComplete | undefined) => void;
  onModelSelectionChange: (v: EquipmentAutoComplete | undefined) => void;
  onEquipmentTypeChange?: (equipmentType: EquipmentType) => void;
}

export const ChatEquipmentSelect = ({
                                      brands,
                                      models,
                                      onBrandSelectionChange,
                                      onModelSelectionChange,
                                      onEquipmentTypeChange
                                    }: IChatEquipmentSelectProps) => {
  const [brandInputValue, setBrandInputValue] = useState('');
  const [modelInputValue, setModelInputValue] = useState('');
  const [isNoResultMessageDisplayed, setIsNoResultMessageDisplayed] = useState(false);

  const brandSelectElement = useRef<HTMLInputElement>(null);
  const modelSelectElement = useRef<HTMLInputElement>(null);

  const showNoResultsMessage = useCallback(() => {
    if (
      brandInputValue
      && modelInputValue
      && (
        !brands?.some(b => b.value === brandInputValue)
        || !models?.some(m => m.value === modelInputValue)
      )
    ) {
      setIsNoResultMessageDisplayed(true);
    } else {
      setIsNoResultMessageDisplayed(false);
    }
  }, [brandInputValue, modelInputValue, brands, models]);

  useEffect(() => {
    if (brandSelectElement.current) {
      const brandSelectElementCurrent = brandSelectElement.current;
      brandSelectElementCurrent.addEventListener('blur', showNoResultsMessage);

      return () => {
        brandSelectElementCurrent.removeEventListener('blur', showNoResultsMessage);
      };
    }
  }, [showNoResultsMessage, brandSelectElement]);

  useEffect(() => {
    if (modelSelectElement.current) {
      const modelSelectElementCurrent = modelSelectElement.current;
      modelSelectElementCurrent.addEventListener('blur', showNoResultsMessage);

      return () => {
        modelSelectElementCurrent.removeEventListener('blur', showNoResultsMessage);
      };
    }
  }, [showNoResultsMessage, modelSelectElement]);

  const inputInstruction = 'Utilisez les flèches HAUT / BAS pour naviguer dans la liste des suggestions. Appuyez sur Entrée pour sélectionner une option. Sur les appareils tactiles, utilisez le balayage pour naviguer et un double appui pour sélectionner une option';

  const getResultCountMessage = (count: number) => {
    if (count === 0) {
      return 'Aucun résultat';
    } else if (count === 1) {
      return '1 résultat';
    } else {
      return `${count} résultats`;
    }
  };

  const brandSelectionChange = (value: EquipmentAutoComplete | undefined) => {
    onBrandSelectionChange(value);
  };

  const modelSelectionChange = (value: EquipmentAutoComplete | undefined) => {
    onModelSelectionChange(value);
  };

  const brandInputValueChange = (value?: string) => {
    setIsNoResultMessageDisplayed(false);
    setBrandInputValue(value || '');
  };

  const modelInputValueChange = (value?: string) => {
    setIsNoResultMessageDisplayed(false);
    setModelInputValue(value || '');
  };

  const equipmentTypeChange = (value: string) => {
    onEquipmentTypeChange?.(value === equipmentType.cooling ? equipmentType.cooling : equipmentType.heating);
  };

  const getEquipmentNotFoundMessage = (brandInputValue: string, modelInputValue: string) => {
    if (!isNoResultMessageDisplayed) {
      return '';
    }

    return (
      <div>
        <NJInlineMessage
          variant="information"
          className={styles['nj-chat-equipment__not-found']}
        >
          Désolé, je ne reconnais pas cette chaudière (<strong>{brandInputValue} - {modelInputValue}</strong>). Pour
          m’aider à m’améliorer si vous avez la notice correspondante, merci de me l’envoyer à cette adresse :&nbsp;
          <a href="mailto:notices.techhelp@engie.com">notices.techhelp@engie.com</a>
        </NJInlineMessage>
      </div>
    );
  };

  return (
    <div className={styles['nj-chat-equipment-select']}>
      <div className={styles['nj-chat-equipment-select__equipment-type']}>
        <NJSegmentedControl
          label="Type d'équipement"
          value={equipmentType.heating}
          size="lg"
          onChange={equipmentTypeChange}
        >
          <NJSegmentedControlButton label="Chaud" value={equipmentType.heating} iconName="local_fire_department" />
          <NJSegmentedControlButton label="Froid" value={equipmentType.cooling} iconName="ac_unit" />
        </NJSegmentedControl>
      </div>

      <div className={styles['nj-chat-equipment-select__selects']}>
        <NJAutocomplete
          ref={brandSelectElement}
          label="Marque"
          listLabel="Suggestions de marques"
          inputInstructions={inputInstruction}
          resultsCountMessage={getResultCountMessage}
          data={brands}
          className={styles['nj-chat-equipment-select__select-item']}
          showNoResultsMessage={false}
          onChange={brandSelectionChange}
          onInputValueChange={brandInputValueChange}
        />

        <NJAutocomplete
          ref={modelSelectElement}
          label="Modèle"
          listLabel="Suggestions de modèles"
          inputInstructions={inputInstruction}
          resultsCountMessage={getResultCountMessage}
          data={models}
          className={styles['nj-chat-equipment-select__select-item']}
          showNoResultsMessage={false}
          onChange={modelSelectionChange}
          onInputValueChange={modelInputValueChange}
        />
      </div>

      {getEquipmentNotFoundMessage(brandInputValue, modelInputValue)}
    </div>
  );
};
